/**
 * BattleDragFlashlight — while the primary mouse button is held after pressing down
 * inside the battle viewport wrapper, adds a soft light cone at the pointer.
 * Optional onBeamSample for throttled network sync to the human opponent.
 */

import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { playSoundEffect } from '../hooks/useSoundEffects';
import {
  buildFlashlightRadialGradientBackground,
  normalizeFlashlightColorForDisplay,
} from '../constants/flashlightColors';
import './BattleDragFlashlight.css';

const FLASHLIGHT_SWITCH_SOUND = '/sounds/switch.mp3';

function isExcludedFlashlightTarget(target, nativeEvent) {
  if (typeof Element === 'undefined') {
    return false;
  }

  const exclusionSelector = [
    '.modal-overlay',
    '.modal-content',
    '.strategy-modal-overlay',
    '.strategy-modal-panel',
    'button',
    'a[href]',
    'input',
    'textarea',
    'select',
    '[role="button"]',
    '[contenteditable="true"]',
    '[data-no-battle-flashlight="true"]',
    // Draggable sources: pressing a hand card or a battlefield unit starts a drag
    // (deploy / attack), so the flashlight must not also activate on them.
    '.carousel-order-card',
    '[data-slot-zone][data-unit-key]',
  ].join(', ');

  const path = typeof nativeEvent?.composedPath === 'function' ? nativeEvent.composedPath() : [];
  for (const node of path) {
    if (node instanceof Element && node.matches(exclusionSelector)) {
      return true;
    }
  }

  if (target instanceof Element) {
    return Boolean(target.closest(exclusionSelector));
  }

  if (target && target.parentElement instanceof Element) {
    return Boolean(target.parentElement.closest(exclusionSelector));
  }

  return false;
}

export function BattleDragFlashlight({
  containerRef,
  onBeamSample,
  colorKey = 'white',
  disabled = false,
}) {
  const [beam, setBeam] = useState(null);
  const beamRef = useRef(null);
  const onBeamSampleRef = useRef(onBeamSample);
  const disabledRef = useRef(disabled);

  useEffect(() => {
    onBeamSampleRef.current = onBeamSample;
  }, [onBeamSample]);

  useEffect(() => {
    beamRef.current = beam;
  }, [beam]);

  useEffect(() => {
    disabledRef.current = disabled;
  }, [disabled]);

  const clearBeam = useCallback(({ silent = false } = {}) => {
    if (beamRef.current != null && !silent) {
      playSoundEffect(FLASHLIGHT_SWITCH_SOUND);
    }
    setBeam(null);
    const cb = onBeamSampleRef.current;
    if (typeof cb === 'function') {
      cb({ active: false });
    }
  }, []);

  // Drop an active beam when targeting/drag modes take over (no switch sound).
  useEffect(() => {
    if (!disabled) return;
    if (beamRef.current != null) {
      clearBeam({ silent: true });
    }
  }, [disabled, clearBeam]);

  useEffect(() => {
    const root = containerRef?.current;
    if (!root) {
      throw new Error('BattleDragFlashlight: containerRef must point to the viewport wrapper element');
    }

    const onPointerDown = (e) => {
      if (disabledRef.current) return;
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      if (!root.contains(e.target)) return;
      if (isExcludedFlashlightTarget(e.target, e)) return;
      playSoundEffect(FLASHLIGHT_SWITCH_SOUND);
      setBeam({ x: e.clientX, y: e.clientY });
      const cb = onBeamSampleRef.current;
      if (typeof cb === 'function') {
        cb({ active: true, clientX: e.clientX, clientY: e.clientY });
      }
    };

    root.addEventListener('pointerdown', onPointerDown, true);
    return () => root.removeEventListener('pointerdown', onPointerDown, true);
  }, [containerRef]);

  useEffect(() => {
    if (!beam) return;

    const onMove = (e) => {
      setBeam({ x: e.clientX, y: e.clientY });
      const cb = onBeamSampleRef.current;
      if (typeof cb === 'function') {
        cb({ active: true, clientX: e.clientX, clientY: e.clientY });
      }
    };

    const onUp = (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      clearBeam();
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);

    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [beam, clearBeam]);

  useEffect(
    () => () => {
      if (beamRef.current != null) {
        playSoundEffect(FLASHLIGHT_SWITCH_SOUND);
        const cb = onBeamSampleRef.current;
        if (typeof cb === 'function') {
          cb({ active: false });
        }
      }
    },
    []
  );

  if (!beam) return null;

  const displayColor = normalizeFlashlightColorForDisplay(colorKey);

  return (
    <div
      className="battle-drag-flashlight-overlay"
      aria-hidden="true"
      style={{
        '--flashlight-x': `${beam.x}px`,
        '--flashlight-y': `${beam.y}px`,
        background: buildFlashlightRadialGradientBackground(displayColor),
      }}
    />
  );
}

/**
 * Renders the opponent’s flashlight using normalized (0–1) coords vs .viewport-wrapper.
 */
export function OpponentBattleFlashlight({ normalized, wrapperRef }) {
  const [, setLayoutTick] = useState(0);

  useLayoutEffect(() => {
    if (!normalized) return;
    setLayoutTick((t) => t + 1);
  }, [normalized]);

  useEffect(() => {
    if (!normalized) return undefined;
    const bump = () => setLayoutTick((t) => t + 1);
    window.addEventListener('resize', bump);
    const el = wrapperRef?.current;
    let ro;
    if (typeof ResizeObserver !== 'undefined' && el) {
      ro = new ResizeObserver(bump);
      ro.observe(el);
    }
    return () => {
      window.removeEventListener('resize', bump);
      if (ro && el) ro.disconnect();
    };
  }, [normalized, wrapperRef]);

  if (!normalized) return null;

  const el = wrapperRef?.current;
  if (!el) return null;

  const rect = el.getBoundingClientRect();
  if (!(rect.width > 0) || !(rect.height > 0)) return null;

  const x = rect.left + normalized.nx * rect.width;
  const y = rect.top + normalized.ny * rect.height;
  const displayColor = normalizeFlashlightColorForDisplay(normalized.color);

  return (
    <div
      className="battle-opponent-flashlight-overlay"
      aria-hidden="true"
      style={{
        '--flashlight-x': `${x}px`,
        '--flashlight-y': `${y}px`,
        background: buildFlashlightRadialGradientBackground(displayColor),
      }}
    />
  );
}

export default BattleDragFlashlight;
